import { Component, Input } from '@angular/core';
import { BaseRecommendationSegmentWithParts } from '../../../../../interfaces/route-recommendations/base-recommendation-segment';
import {
  RouteRecSegmentPartType,
  RouteRecSegmentType,
} from '../../../../../enums/algo-travel-mode.enum';
import { animate, state, style, transition, trigger } from '@angular/animations';
import { VmeDirectivesModule } from '../../../../../vme-directives.module';
import { VmePipesModule } from '../../../../../vme-pipes.module';
import { ButtonModule, DialogModule } from 'primeng';
import { CommonModule } from '@angular/common';

const CostHiddenSegmentTypes: RouteRecSegmentType[] = [
  RouteRecSegmentType.Car,
  RouteRecSegmentType.Cycle,
  RouteRecSegmentType.Walk,
];

// Zero travel time on these means the next task is at the same site, not a real leg to show.
const SameLocationSegmentTypes: string[] = [RouteRecSegmentType.Walk, RouteRecSegmentType.Cycle];

const animationTiming = '400ms ease-in-out';

const closedStateStyles = style({
  height: '0px',
  opacity: 0,
  overflow: 'hidden',
  marginTop: '0px',
  marginBottom: '0px',
  paddingTop: '0px',
  paddingBottom: '0px',
});

const openStateStyles = style({
  height: '*',
  opacity: 1,
  marginTop: '10px',
  marginBottom: '10px',
  paddingTop: '0px',
  paddingBottom: '0px',
});

@Component({
  selector: 'route-itinerary-travel-guide',
  templateUrl: './route-itinerary-travel-guide.component.html',
  styleUrls: [
    './route-itinerary-travel-guide.component.scss',
    '../../common/scss/route-itinerary-common.scss',
  ],
  animations: [
    trigger('slideInOut', [
      state('closed', closedStateStyles),
      state('open', openStateStyles),
      transition('closed <=> open', [animate(animationTiming)]),
    ]),
  ],
  standalone: true,
  imports: [VmeDirectivesModule, VmePipesModule, ButtonModule, DialogModule, CommonModule],
})
export class RouteItineraryTravelGuideComponent {
  @Input() transportInfo: BaseRecommendationSegmentWithParts;

  public isDetailsOpen: boolean = false;
  public showAdditionalTransportHelper: boolean = false;

  protected readonly RouteRecSegmentPartType = RouteRecSegmentPartType;

  constructor() {}

  get costString() {
    if (!this.transportInfo) {
      return '';
    }

    if (this.transportInfo.travelCostLowerBound && this.transportInfo.travelCostUpperBound) {
      return `£${this.transportInfo.travelCostLowerBound} - £${this.transportInfo.travelCostUpperBound}`;
    }

    return `£${this.transportInfo.travelCost}`;
  }

  get travelModes() {
    return this.transportInfo?.segmentParts?.map((x) => x.transportType);
  }

  get displayCost() {
    return (
      !CostHiddenSegmentTypes.map((x) => x.toUpperCase()).includes(
        this.transportInfo.segmentType.toUpperCase(),
      ) && false
    ); // Temp added to always hide segment level travel costs until the Algo can generate more accurate PT ranges.
  }

  get firstPart() {
    if (!this.transportInfo.segmentParts || this.transportInfo.segmentParts.length === 0) {
      return null;
    }
    return this.transportInfo.segmentParts[0];
  }

  get getPrimaryLineIcon(): string {
    let transportType = this.firstPart?.transportType ?? this.transportInfo.segmentType;

    return this.getLineIconClass(transportType);
  }

  get getPrimaryLineSVGIcon(): string {
    let transportType = this.firstPart?.transportType ?? this.transportInfo.segmentType;

    return this.getLineSVGIconUri(transportType);
  }

  get isChangeTaskAtSameLocation(): boolean {
    return (
      SameLocationSegmentTypes.includes(this.transportInfo.segmentType) &&
      this.transportInfo.travelTime === 0
    );
  }

  get transportOptionsText(): string {
    const count = this.transportInfo.resultCount;
    return `${Math.max(3, count)} Transport Options`;
  }

  getLineIconClass(transportType: string): string {
    switch (transportType) {
      case RouteRecSegmentPartType.Cycle:
        return 'fa fa-bicycle';
      case RouteRecSegmentPartType.Taxi:
        return 'fa fa-taxi';
      case RouteRecSegmentPartType.Bus:
      case RouteRecSegmentPartType.Coach:
        return 'fa fa-bus';
      case RouteRecSegmentPartType.Ferry:
        return 'fa fa-ship';
      default:
        return 'fa fa-question-circle';
    }
  }

  getLineSVGIconUri(transportType: string): string {
    switch (transportType) {
      case RouteRecSegmentPartType.Train:
        return '/assets/images/svg/national-rail.svg';
      case RouteRecSegmentPartType.Overground:
        return '/assets/images/svg/london-underground.svg';
      case RouteRecSegmentPartType.Underground:
        return '/assets/images/svg/london-underground.svg';
      case RouteRecSegmentPartType.Walk:
        return '/assets/images/svg/solid_walking.svg';
      default:
        return null;
    }
  }

  toggleDetails(): void {
    this.isDetailsOpen = !this.isDetailsOpen;
  }

  protected readonly RouteRecSegmentType = RouteRecSegmentType;
}
